import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { Palette, Sun, Moon, Monitor, Check } from "lucide-react";

const ACCENTS = [
  { value: "navy", label: "Navy", color: "#001A4B" },
  { value: "crimson", label: "Crimson", color: "#8c1d2c" },
  { value: "forest", label: "Forest", color: "#14532d" },
  { value: "ocean", label: "Ocean", color: "#075985" },
];
const THEMES = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export default function ThemeMenu({ tone = "light" }) {
  const { theme, accent, setTheme, setAccent } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const btnTone = tone === "light" ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground";

  return (
    <div className="relative" ref={ref}>
      <button data-testid="theme-menu-button" onClick={() => setOpen((o) => !o)} title="Color scheme" aria-label="Color scheme" className={`p-1.5 rounded-md transition-colors ${btnTone}`}>
        <Palette className="h-5 w-5" />
      </button>
      {open && (
        <div data-testid="theme-menu-panel" className="absolute right-0 mt-2 w-56 bg-card text-foreground border border-border rounded-lg shadow-xl p-3 z-50">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Mode</div>
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {THEMES.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.value} data-testid={`theme-${t.value}`} onClick={() => setTheme(t.value)}
                  className={`flex flex-col items-center gap-1 py-2 rounded-md border text-xs transition-colors ${theme === t.value ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:bg-muted"}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Accent</div>
          <div className="flex gap-2.5">
            {ACCENTS.map((a) => (
              <button key={a.value} data-testid={`accent-${a.value}`} onClick={() => setAccent(a.value)} title={a.label}
                className={`relative h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${accent === a.value ? "border-foreground" : "border-transparent"}`}
                style={{ backgroundColor: a.color }}>
                {accent === a.value && <Check className="h-4 w-4 text-white absolute inset-0 m-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
